import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { MonthlyStatement, MonthlyStatementParams, ExportFormat } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const useMonthlyStatement = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const [report, setReport] = useState<MonthlyStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<MonthlyStatementParams>({ month: currentMonth() });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportRepo.getMonthlyStatement(params);
      setReport(data);
    } catch (e) {
      console.error('Error loading monthly statement:', e);
      toast.error('Error al cargar el estado financiero mensual');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(
    async (format: ExportFormat) => {
      setExporting(true);
      try {
        const blob = await reportRepo.exportMonthlyStatement(format, params);
        const filename = generateReportFilename(`estado-financiero-${params.month}`, format);
        downloadBlob(blob, filename);
        toast.success('Reporte exportado correctamente');
      } catch (e) {
        console.error('Error exporting monthly statement:', e);
        toast.error('Error al exportar el reporte');
      } finally {
        setExporting(false);
      }
    },
    [reportRepo, params, toast],
  );

  const setMonth = useCallback((month: string) => {
    setParams((prev) => ({ ...prev, month }));
  }, []);

  const setManualFields = useCallback((fields: Partial<MonthlyStatementParams>) => {
    setParams((prev) => ({ ...prev, ...fields }));
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    loading,
    exporting,
    params,
    setMonth,
    setManualFields,
    refetch: fetchReport,
    exportReport,
  };
};
